/**
 * Shop-side client for the Payload CMS content API (STORY-007,
 * STORY-011). Thin wrapper around `fetch` — no runtime deps.
 *
 * All collections that the shop consumes are keyed to the Symfony
 * catalog. Right now only `CategoryContent` is wired; pages / posts /
 * services read paths follow the same shape.
 */

function baseUrl(): string {
  // Direct `import.meta.env.VITE_*` access — Vite statically replaces
  // it at build time. A cast like `(import.meta as {...}).env` breaks
  // that replacement and the env never reaches the client (learned
  // the hard way on STORY-F-7 follow-up). ImportMetaEnv typing lives
  // in src/vite-env.d.ts.
  const fromEnv =
    import.meta.env?.VITE_PAYLOAD_API_URL ??
    (typeof process !== 'undefined' ? process.env?.PAYLOAD_API_URL : undefined)
  if (fromEnv) return stripTrailingSlash(fromEnv)
  // SSR: reach CMS directly via Docker-internal hostname.
  // Browser: use the Vite dev-server proxy so the request stays same-origin
  // and avoids CORS issues when accessing the shop from a non-localhost host.
  return typeof window === 'undefined' ? 'http://gcg_cms:3000' : '/cms-api'
}

function stripTrailingSlash(s: string): string {
  return s.replace(/\/$/, '')
}

export interface CategoryContent {
  id: string
  symfonyCategorySlug: string
  seoTitle: string
  seoDescription: string
  heroImage?: { id: string; url: string; alt?: string } | null
  body?: unknown[] // Payload blocks — rendered by the block renderer
  featuredProducts?: Array<{ mpn: string }>
  lastReviewedBy?: { id: string; name: string } | null
  lastReviewedAt?: string | null
  updatedAt: string
}

export interface Page {
  id: string
  title: string
  slug: string
  site: 'shop' | 'site'
  layout: unknown[]
  seo: {
    title?: string | null
    description?: string | null
    image?: { url?: string | null; alt?: string | null } | null
  }
  updatedAt: string
}

export interface NavItem {
  label: string
  href: string
}

export interface Navigation {
  mainNav: NavItem[]
  footerNav: NavItem[]
  updatedAt: string
}

/**
 * Fetch the singleton Navigation global from Payload CMS.
 * Returns null when the CMS is unreachable — callers fall back to hardcoded defaults.
 * Payload exposes globals at GET /api/globals/<slug>.
 * Pass `locale` to get translated labels (defaults to Payload's default locale).
 */
export async function fetchNavigation(
  opts: { signal?: AbortSignal; baseUrlOverride?: string; locale?: string } = {},
): Promise<Navigation | null> {
  const base = opts.baseUrlOverride ? stripTrailingSlash(opts.baseUrlOverride) : baseUrl()
  const localeParam = opts.locale ? `&locale=${encodeURIComponent(opts.locale)}` : ''
  const url = base + `/api/globals/navigation?depth=0${localeParam}`

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: opts.signal,
    })
    if (!res.ok) {
      if (typeof console !== 'undefined') {
        console.warn('[cms] navigation lookup failed', res.status)
      }
      return null
    }
    return (await res.json()) as Navigation
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[cms] navigation unreachable', err)
    }
    return null
  }
}

/**
 * Fetch a CMS page by slug and site. Returns null when not found or CMS
 * is unreachable — callers throw a 404 rather than surfacing an error page.
 */
export async function fetchPage(
  slug: string,
  site: 'shop' | 'site',
  opts: { signal?: AbortSignal; baseUrlOverride?: string } = {},
): Promise<Page | null> {
  const base = opts.baseUrlOverride ? stripTrailingSlash(opts.baseUrlOverride) : baseUrl()
  const url =
    base +
    '/api/pages?where[slug][equals]=' +
    encodeURIComponent(slug) +
    '&where[site][equals]=' +
    encodeURIComponent(site) +
    '&depth=2&limit=1'

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: opts.signal,
    })
    if (!res.ok) {
      if (typeof console !== 'undefined') {
        console.warn('[cms] pages lookup failed', slug, res.status)
      }
      return null
    }
    const body = (await res.json()) as { docs?: Page[] }
    return body.docs?.[0] ?? null
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[cms] pages unreachable', slug, err)
    }
    return null
  }
}

/**
 * Fetch a CategoryContent entry by the shop's category slug. Returns
 * null when no content exists yet (UI falls back to bare catalog data),
 * and ALSO when the CMS is unreachable (network / 5xx). The latter is
 * logged to console in dev but never surfaces to the user — a degraded
 * category page is better than an error page.
 */
export async function fetchCategoryContent(
  slug: string,
  opts: { signal?: AbortSignal; baseUrlOverride?: string } = {},
): Promise<CategoryContent | null> {
  const base = opts.baseUrlOverride ? stripTrailingSlash(opts.baseUrlOverride) : baseUrl()
  const url =
    base +
    '/api/category-content?where[symfonyCategorySlug][equals]=' +
    encodeURIComponent(slug) +
    '&depth=2&limit=1'

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: opts.signal,
    })
    if (!res.ok) {
      if (typeof console !== 'undefined') {
        console.warn('[cms] category-content lookup failed', slug, res.status)
      }
      return null
    }
    const body = (await res.json()) as { docs?: CategoryContent[] }
    return body.docs?.[0] ?? null
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[cms] category-content unreachable', slug, err)
    }
    return null
  }
}
